/**
 * The door, on screen.
 *
 * Two screens and no more: *sign in*, and *choose what you are called*. They
 * appear in front of a player exactly once each — the first time they take a
 * seat in a room — and never again on that machine, because the token is kept
 * and the account outlives it.
 *
 * There is no password field here, and there must never be one. The player signs
 * in on the identity provider's own page; this game never sees a password, never
 * stores one, and cannot leak one. That is the whole reason for the redirect
 * dance in `src/net/auth.ts`, and the reason "create an account" here means
 * choosing a name rather than filling in a form.
 *
 * It is deliberately its own overlay rather than a screen inside the menu: the
 * menu is a list of things to *play*, and this is a question about who is
 * playing them. It also means the gate sits where the rule sits — in the path
 * that opens a room — rather than depending on which button somebody pressed.
 */
import type { AccountService } from '../net/accounts.ts';
import type { SignIn } from '../net/auth.ts';

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

const SHELL =
  'position:fixed;inset:0;z-index:400;display:flex;align-items:center;justify-content:center;' +
  'background:rgba(8,7,6,0.82);backdrop-filter:blur(3px);font-family:Inter,system-ui,sans-serif';
const CARD =
  'width:min(440px,92vw);padding:28px 30px 24px;background:#14120f;border:1px solid #2a2724;' +
  'border-radius:4px;box-shadow:0 24px 70px rgba(0,0,0,0.6);color:#a89e8c';
const TITLE =
  'font-family:Cinzel,Georgia,serif;font-size:21px;color:#d9cfbc;margin:0 0 6px;letter-spacing:.01em';
const SUB = 'font-size:13px;line-height:1.55;margin:0 0 18px;color:#8c8477';
const FIELD =
  'width:100%;box-sizing:border-box;padding:10px 12px;background:#0e0d0b;border:1px solid #33302b;' +
  'border-radius:3px;color:#e6dcc8;font:14px Inter,system-ui,sans-serif;outline:none';
const SEAL =
  'display:inline-block;padding:9px 18px;background:#8a6c26;color:#14120f;border:0;border-radius:3px;' +
  'font:600 13px Inter,system-ui,sans-serif;cursor:pointer;letter-spacing:.02em';
const QUIET =
  'display:inline-block;padding:9px 14px;background:transparent;color:#8c8477;border:0;' +
  'font:13px Inter,system-ui,sans-serif;cursor:pointer';
const ERR = 'min-height:18px;margin:10px 0 0;font-size:12.5px;color:#9c3336';

/**
 * The overlay, torn down whichever way it ends.
 *
 * One at a time, always: signing out while the door is already shut would
 * otherwise stack a second card on the first, and the player would dismiss one
 * and find another underneath. Whatever is up is taken down first.
 */
let showing: HTMLElement | null = null;
function overlay(inner: string): { root: HTMLElement; close: () => void } {
  showing?.remove();
  const root = document.createElement('div');
  root.style.cssText = SHELL;
  root.innerHTML = `<div style="${CARD}">${inner}</div>`;
  document.body.appendChild(root);
  showing = root;
  return {
    root,
    close: () => {
      root.remove();
      if (showing === root) showing = null;
    },
  };
}

/**
 * Make sure there is an account behind this player, and do not take no for an
 * answer.
 *
 * On a hearth that asks for accounts this is a wall, not a question: there is no
 * way past it that leaves somebody playing. The first draft gave both cards a
 * "Not now", which made the whole thing a suggestion — a player could decline
 * and carry on, which is exactly what a forced sign-in is not.
 *
 * What a player *can* do is leave: the name card offers "Sign out", which takes
 * them back to the door rather than through it. Being able to stop is not the
 * same as being able to walk past, and a screen with no exit at all is a trap.
 *
 * The loop is the shape of that. Sign in, take a name, and if they sign out
 * halfway through the second we are back at the first. It ends when there is an
 * account, or when the tab leaves for the identity provider and this page stops
 * existing.
 */
export async function ensureAccount(
  signIn: SignIn,
  accounts: AccountService,
): Promise<string | null> {
  if (!signIn.configured) return null;
  for (;;) {
    if (!signIn.signedIn) {
      // Does not resolve in the ordinary case: pressing the only button on it
      // sends the tab to the provider, and `finish()` picks it up coming back.
      await askToSignIn(signIn, signIn.who?.name ?? '');
      return null;
    }
    const held = await accounts.mine().catch(() => null);
    if (held) return held.handle;
    const taken = await askForName(signIn, accounts);
    if (taken) return taken;
    // Signed out from the name card. Round we go, back to the door.
  }
}

/**
 * The door. One button, and it is not "later".
 *
 * The promise it returns is deliberately never resolved by the happy path —
 * `signIn.begin()` navigates away, and a card that resolved first would flash a
 * menu the player is not allowed to use on its way out. It resolves only if the
 * provider cannot be reached, so the player is left looking at the reason rather
 * than at nothing.
 */
function askToSignIn(signIn: SignIn, known: string): Promise<void> {
  return new Promise((resolve) => {
    const { root } = overlay(
      `<h2 style="${TITLE}">Who is at the door?</h2>` +
        `<p style="${SUB}">This hearth is played by people it knows. Your name, your runs and ` +
        `the rooms you have sat in are kept against an account, so it will know you ` +
        `tomorrow, on this machine or any other${known ? `, ${esc(known)}` : ''}.</p>` +
        `<p style="${SUB};margin-bottom:20px">You will be taken to the identity provider's own page. ` +
        `This game never sees your password.</p>` +
        `<div style="display:flex;gap:10px;align-items:center">` +
        `<button id="go" style="${SEAL}">Sign in</button></div>` +
        `<p id="err" style="${ERR}"></p>`,
    );
    const go = root.querySelector('#go') as HTMLButtonElement;
    const err = root.querySelector('#err') as HTMLElement;
    go.onclick = () => {
      go.disabled = true;
      go.textContent = 'Taking you there…';
      signIn.begin().catch((e: Error) => {
        err.textContent = e.message;
        go.disabled = false;
        go.textContent = 'Sign in';
        resolve();
      });
    };
  });
}

/**
 * The second screen: what shall we call you?
 *
 * This is account creation, and everything it needs is already in hand — the
 * provider has said who this person is, and the only thing the game does not
 * know is the name they want the other crowns to see. The module decides
 * whether they may have it; this screen just carries the answer back.
 */
function askForName(
  signIn: SignIn,
  accounts: AccountService,
  /** The name they have now, when this is a change of name rather than a first one. */
  current?: string,
): Promise<string | null> {
  const renaming = current !== undefined;
  const suggestion = renaming
    ? current
    : (signIn.who?.name || signIn.who?.email?.split('@')[0] || '').slice(0, 20);
  return new Promise((resolve) => {
    const { root, close } = overlay(
      `<h2 style="${TITLE}">${renaming ? 'Change your name' : 'Take a name'}</h2>` +
        `<p style="${SUB}">It is yours alone on this hearth, and it is what the other crowns ` +
        `will see.${renaming ? ' The rooms you have played in keep their history.' : ' You can change it later.'}</p>` +
        `<input id="handle" style="${FIELD}" maxlength="20" autocomplete="off" spellcheck="false" ` +
        `value="${esc(suggestion ?? '')}" placeholder="Ada of the Vale">` +
        `<p id="err" style="${ERR}"></p>` +
        `<div style="display:flex;gap:10px;align-items:center;margin-top:14px">` +
        `<button id="go" style="${SEAL}">${renaming ? 'Change it' : 'Take it'}</button>` +
        // Changing a name you already have is optional, so that one may be
        // waved away. Taking your first is not: the only way out of it is out.
        `<button id="no" style="${QUIET}">${renaming ? 'Leave it' : 'Sign out'}</button></div>`,
    );
    const input = root.querySelector('#handle') as HTMLInputElement;
    const err = root.querySelector('#err') as HTMLElement;
    const go = root.querySelector('#go') as HTMLButtonElement;
    const no = root.querySelector('#no') as HTMLButtonElement;

    const finish = (handle: string | null): void => {
      close();
      resolve(handle);
    };
    const attempt = async (): Promise<void> => {
      const handle = input.value.trim();
      err.textContent = '';
      go.disabled = true;
      try {
        if (renaming) await accounts.rename(handle);
        else await accounts.create(handle);
        finish(handle);
      } catch (e) {
        // The module's refusals are sentences meant for a player — "somebody
        // already goes by that name" — so they are shown as they arrive.
        err.textContent = (e as Error).message;
        go.disabled = false;
        input.focus();
      }
    };
    go.onclick = () => void attempt();
    no.onclick = () => {
      // Taking a first name is not optional, so the second button is the way
      // *out* rather than the way past: it gives up the sign-in and returns to
      // the door. `ensureAccount` loops straight back to it.
      if (!renaming) signIn.signOut();
      finish(null);
    };
    input.onkeydown = (ev) => {
      // The overlay owns the keyboard while it is up: the game's hotkeys are
      // listening on the window and would build a granary while somebody types.
      ev.stopPropagation();
      if (ev.key === 'Enter') void attempt();
      // Escape closes a card you asked for. It does not close the one standing
      // between somebody and a game they have not signed in to play.
      if (ev.key === 'Escape' && renaming) finish(null);
    };
    input.onkeyup = (ev) => ev.stopPropagation();
    setTimeout(() => input.focus(), 0);
  });
}

/**
 * Tell somebody their sign-in did not work out.
 *
 * A failed return from the provider is the one moment in this flow where a
 * player is looking at a screen that cannot explain itself — they clicked "sign
 * in", went somewhere, and came back to a title screen. So it says what
 * happened, and the sentence comes from the provider.
 */
export function signInFailed(why: string): void {
  const { root, close } = overlay(
    `<h2 style="${TITLE}">That sign-in did not finish</h2>` +
      `<p style="${SUB}">${esc(why)}</p>` +
      `<button id="ok" style="${SEAL}">Very well</button>`,
  );
  (root.querySelector('#ok') as HTMLButtonElement).onclick = close;
}

/**
 * The account, on the title screen.
 *
 * Until this, signing in was something that happened *to* a player: they pressed
 * co-op, a card appeared, they went to another site and came back, and the only
 * evidence any of it had worked was that the room let them in. There was no way
 * to see who you were, no way to stop being them, and no way to change the name
 * you had chosen in a hurry.
 *
 * So it is a fixed corner of the title screen, in the manner of the room panel
 * the net layer already owns. Deliberately *not* a screen inside the menu: the
 * menu is a list of things to play, this is a question about who is playing
 * them, and — practically — the menu is being rewritten in a parallel branch,
 * so a change that needs none of its markup is a change that cannot collide
 * with it.
 *
 * It shows nothing at all on a hearth that does not ask for accounts. There is
 * no point offering to sign in to a server that would not know what to do with
 * it.
 */
export class AccountBar {
  private el: HTMLElement | null = null;
  private menu: HTMLElement | null = null;
  private handle: string | null = null;
  private busy = false;
  private shown = false;

  constructor(
    private readonly signIn: SignIn,
    private readonly accounts: AccountService,
    /**
     * Told whenever who-you-are changes, including to nobody.
     *
     * The gate in front of the play buttons keeps the handle it was given, so it
     * asks the network once rather than on every click — which means signing out
     * *here* has to reach it, or a signed-out player would still be holding the
     * door open with a name they no longer have.
     */
    private readonly onAccount: (handle: string | null) => void = () => {},
  ) {}

  /**
   * Put it on screen and find out who this is.
   *
   * The account is read once, here, rather than on every paint: it is a request
   * over the network, and the answer only changes when this player changes it.
   */
  async mount(): Promise<void> {
    if (!this.signIn.configured) return;
    this.el = document.createElement('div');
    this.el.id = 'account-bar';
    this.el.style.cssText =
      'position:fixed;top:12px;right:14px;z-index:300;display:none;align-items:center;gap:9px;' +
      'font:12.5px Inter,system-ui,sans-serif;color:#a89e8c';
    document.body.appendChild(this.el);
    await this.reload();
  }

  /**
   * Told a name from outside — because the bar is not the only place one is
   * taken.
   *
   * The door raises the same card the corner's menu does, and when a player
   * takes their name *there*, the bar knew nothing about it: it went on saying
   * "take a name" until the page was reloaded. This is the one line that was
   * missing, and no round trip: whoever took the name already knows what it is.
   */
  told(handle: string | null): void {
    if (handle === this.handle) return;
    this.handle = handle;
    this.paint();
  }

  /** Only over the title screen: in a run, the room panel is the one that speaks. */
  setVisible(on: boolean): void {
    this.shown = on;
    if (this.el) this.el.style.display = on ? 'flex' : 'none';
    if (!on) this.closeMenu();
  }

  /** Who the module says this is, and then repaint. */
  private async reload(): Promise<void> {
    this.handle = this.signIn.signedIn ? ((await this.accounts.mine().catch(() => null))?.handle ?? null) : null;
    this.onAccount(this.handle);
    this.paint();
  }

  private paint(): void {
    if (!this.el) return;
    this.el.style.display = this.shown ? 'flex' : 'none';
    if (!this.signIn.signedIn) {
      this.el.innerHTML =
        `<button id="in" style="${SEAL};padding:7px 14px;font-size:12px">Sign in</button>`;
      (this.el.querySelector('#in') as HTMLButtonElement).onclick = () => void this.begin();
      return;
    }
    // Signed in with no account is a real state, and the bar is where somebody
    // notices it: the provider knows them, this hearth does not yet.
    const label = this.handle ?? 'take a name';
    const tint = this.handle ? '#d9cfbc' : '#c8a44c';
    this.el.innerHTML =
      `<span style="color:#6d675e">●</span>` +
      `<button id="who" style="${QUIET};padding:6px 8px;color:${tint};font-size:12.5px">${esc(label)}</button>`;
    (this.el.querySelector('#who') as HTMLButtonElement).onclick = (ev) => {
      ev.stopPropagation();
      this.toggleMenu();
    };
  }

  private toggleMenu(): void {
    if (this.menu) {
      this.closeMenu();
      return;
    }
    const items: [string, () => void][] = this.handle
      ? [
          ['Change name', () => void this.rename()],
          ['Sign out', () => this.out()],
        ]
      : [
          ['Take a name', () => void this.name()],
          ['Sign out', () => this.out()],
        ];
    const menu = document.createElement('div');
    menu.style.cssText =
      'position:fixed;top:40px;right:14px;z-index:301;min-width:150px;padding:5px;' +
      'background:#14120f;border:1px solid #2a2724;border-radius:3px;' +
      'box-shadow:0 12px 30px rgba(0,0,0,0.5);display:flex;flex-direction:column';
    for (const [label, act] of items) {
      const b = document.createElement('button');
      b.textContent = label;
      b.style.cssText =
        'background:transparent;border:0;color:#a89e8c;text-align:left;padding:8px 10px;' +
        'font:12.5px Inter,system-ui,sans-serif;cursor:pointer;border-radius:2px';
      b.onmouseenter = () => (b.style.background = 'rgba(200,164,76,0.10)');
      b.onmouseleave = () => (b.style.background = 'transparent');
      b.onclick = () => {
        this.closeMenu();
        act();
      };
      menu.appendChild(b);
    }
    document.body.appendChild(menu);
    this.menu = menu;
    // A click anywhere else closes it, which is what every menu in every
    // application does and what a player will try first.
    setTimeout(() => document.addEventListener('pointerdown', this.away, { once: true }), 0);
  }

  private away = (): void => this.closeMenu();

  private closeMenu(): void {
    this.menu?.remove();
    this.menu = null;
    document.removeEventListener('pointerdown', this.away);
  }

  private async begin(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.signIn.begin(); // the tab leaves and comes back through `finish`
    } catch (e) {
      signInFailed((e as Error).message);
      this.busy = false;
    }
  }

  private async name(): Promise<void> {
    const taken = await askForName(this.signIn, this.accounts);
    if (taken) await this.reload();
  }

  private async rename(): Promise<void> {
    const changed = await askForName(this.signIn, this.accounts, this.handle ?? '');
    if (changed) await this.reload();
  }

  /**
   * Sign out, here and not at the provider.
   *
   * This forgets the tokens, which is what "not me any more" means on a shared
   * machine playing a game. It deliberately does not end the session at the
   * identity provider — that is their account, used by whatever else they have
   * signed into with it, and a game has no business logging somebody out of it.
   */
  private out(): void {
    this.signIn.signOut();
    this.handle = null;
    this.onAccount(null);
    this.paint();
  }
}
